export type CommentLinkSegment =
  | { type: 'text', value: string }
  | { type: 'link', value: string, href: string }

const URL_REGEX = /(https?:\/\/[^\s]+)/g
// destination allows one level of balanced parentheses, e.g. /wiki/Function_(mathematics)
const MARKDOWN_LINK_REGEX = /\[([^\]\r\n]+)\]\((https?:\/\/(?:[^\s()]|\([^\s()]*\))+)\)/g
const TRAILING_PUNCTUATION = /[.,;:!?'"]+$/
const CLOSER_TO_OPENER: Record<string, string> = { ')': '(', ']': '[' }

const countChar = (value: string, char: string): number => value.split(char).length - 1

// strips trailing punctuation, but keeps ')' / ']' that close a bracket opened inside the URL
const trimTrailingPunctuation = (rawUrl: string): string => {
  let url = rawUrl

  for (;;) {
    const trailing = url.match(TRAILING_PUNCTUATION)
    if (trailing) {
      url = url.slice(0, -trailing[0].length)
      continue
    }

    const opener = CLOSER_TO_OPENER[url.slice(-1)]
    if (opener && countChar(url, url.slice(-1)) > countChar(url, opener)) {
      url = url.slice(0, -1)
      continue
    }

    return url
  }
}

const parseBareUrls = (text: string): CommentLinkSegment[] => {
  if (!text) return []

  const segments: CommentLinkSegment[] = []
  let cursor = 0

  for (const match of text.matchAll(URL_REGEX)) {
    const rawUrl = match[0]
    const start = match.index ?? 0

    const url = trimTrailingPunctuation(rawUrl)

    if (start > cursor) {
      segments.push({ type: 'text', value: text.slice(cursor, start) })
    }
    segments.push({ type: 'link', value: url, href: url })
    cursor = start + url.length
  }

  if (cursor < text.length) {
    segments.push({ type: 'text', value: text.slice(cursor) })
  }

  return segments
}

export const parseCommentLinks = (text: string): CommentLinkSegment[] => {
  if (!text) return []

  const segments: CommentLinkSegment[] = []
  let cursor = 0

  for (const match of text.matchAll(MARKDOWN_LINK_REGEX)) {
    const label = match[1]
    const href = match[2]
    if (label === undefined || href === undefined) continue

    const start = match.index ?? 0

    if (start > cursor) {
      segments.push(...parseBareUrls(text.slice(cursor, start)))
    }
    segments.push({ type: 'link', value: label, href })
    cursor = start + match[0].length
  }

  if (cursor < text.length) {
    segments.push(...parseBareUrls(text.slice(cursor)))
  }

  return segments
}
